/**
 * Startup Failure Codes for FULL_STARTUP_FAILURE events
 *
 * Note: These are failure codes within the startup saga. Other error types
 * like JS_EXECUTION_ERROR, PUSH_TOKEN_FAILURE, etc. are separate event types
 * triggered by different parts of the application (ErrorBoundary, push token registration).
 */
export enum StartupFailureCode {
  VERSION_NOT_SUPPORTED = 'VERSION_NOT_SUPPORTED',
  EXCHANGE_TOKEN_FAILURE = 'EXCHANGE_TOKEN_FAILURE',
  NETWORK_FAILURE = 'NETWORK_FAILURE',
  MESSAGING_CREDENTIALS_FAILURE = 'MESSAGING_CREDENTIALS_FAILURE',
  UNKNOWN_FAILURE = 'UNKNOWN_FAILURE',
}

export interface StartupFailurePayload {
  error: string
  code: StartupFailureCode
  message: string
}

export function createVersionNotSupportedFailure(): StartupFailurePayload {
  return {
    error: 'App version not supported',
    code: StartupFailureCode.VERSION_NOT_SUPPORTED,
    message: 'App version is below minimum supported version',
  }
}

function createExchangeTokenFailure(error: string): StartupFailurePayload {
  return {
    error,
    code: StartupFailureCode.EXCHANGE_TOKEN_FAILURE,
    message: 'Startup process failed. Error while attempting to login via token exchange',
  }
}

function createUnknownFailure(error: string): StartupFailurePayload {
  return {
    error,
    code: StartupFailureCode.UNKNOWN_FAILURE,
    message: 'Startup process failed',
  }
}

function createNetworkFailure(error: string): StartupFailurePayload {
  return {
    error,
    code: StartupFailureCode.NETWORK_FAILURE,
    message: 'Network connectivity failed during startup',
  }
}

function createMessagingCredentialsFailure(error: string): StartupFailurePayload {
  return {
    error,
    code: StartupFailureCode.MESSAGING_CREDENTIALS_FAILURE,
    message: 'Failed to retrieve messaging credentials during startup',
  }
}

export function classifyStartupFailure(error: Error | string): StartupFailurePayload {
  const errorMessage = typeof error === 'string' ? error : error.message || String(error)
  const errorName = typeof error === 'object' && error !== null ? error.name : undefined
  const problem = typeof error === 'object' && error !== null ? (error as any).problem : undefined

  // Check for network errors (highest priority as they're most specific)
  const isNetworkError =
    // apisauce problem codes
    problem === 'NETWORK_ERROR' ||
    problem === 'TIMEOUT_ERROR' ||
    problem === 'CONNECTION_ERROR' ||
    // Error name properties
    errorName === 'NETWORK_ERROR' ||
    errorName === 'TIMEOUT_ERROR' ||
    // Timeout from withTimeout helper
    errorMessage === 'Timeout' ||
    // Error message patterns
    errorMessage?.toLowerCase().includes('network') ||
    errorMessage?.includes('ECONNREFUSED') ||
    errorMessage?.includes('ECONNRESET') ||
    errorMessage?.includes('ENOTFOUND') ||
    errorMessage?.includes('ETIMEDOUT')

  if (isNetworkError) {
    return createNetworkFailure(errorMessage)
  }

  // Check for exchange token errors
  if (errorMessage.includes('Failed login with exchange token due to API error')) {
    return createExchangeTokenFailure(errorMessage)
  }

  // Check for messaging credentials errors
  if (errorMessage.includes('Failed requesting messaging credentials')) {
    return createMessagingCredentialsFailure(errorMessage)
  }

  // Default to unknown failure
  return createUnknownFailure(errorMessage)
}
